/**
 * The signed-in provider's entitled IPAs (and the provider NPIs granted under
 * each), as returned by PP-API's `ProviderOverview/EntitledIpas`. Response is
 * camelCase (PP-API's global System.Text.Json default).
 */
export interface EntitledIpaEntry {
  ipa: string;
  providerNpis: string[];
}

/**
 * Thrown by `fetchEntitledIpas` on a non-OK response, carrying the HTTP
 * `status` so callers can distinguish a fail-closed BACKEND DENIAL (403 —
 * the account is authenticated but not entitled to a worklist, e.g. a USER
 * MANAGER "view as" peek) from a transient/network failure (401/5xx/network),
 * which should stay retryable. See useWorklistAccessGate.ts's `ipa-forbidden`
 * vs `ipa-error` status split.
 */
export class EntitledIpasError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`EntitledIpas request failed: ${status}`);
    this.name = "EntitledIpasError";
    this.status = status;
  }
}

/**
 * Fetches the signed-in provider's entitled IPAs from PP-API.
 *
 * WHY this exists: the Patient Priority Index worklist gate needs the
 * provider's own IPA(s) to scope the panel query, but AstraConnect can't derive
 * them locally — member_360 CONFIG has no per-user IPA storage and
 * care-management-gql leaves `connectCurrentUser…permissionMeta.ipas` undefined.
 * So the IPA(s) are sourced from the provider's Provider Portal identity via
 * this endpoint (see ppi-astraconnect-migration-plan, 2026-07-24 IPA-source
 * decision). Companion to the PatientPriorityIndex entitlement gate.
 *
 * Identity is the bearer itself — PP-API reads the caller's oid from it; there
 * is NO request body and NO client-supplied IPA. Bare token (no "Bearer "
 * prefix), matching every other host PP-API call (auth/ppEligibility.ts) and the
 * @astrana/ui module's own baseQuery. No `X-Backend-Id`: this endpoint reads the
 * PortalManager grant table directly (not tenant-scoped), and an IPA header is
 * exactly the value we're asking the endpoint to tell us.
 *
 * Rejects on a non-OK response (401/403/5xx/network) so the caller can surface a
 * distinct error/retry state rather than mistaking a failed fetch for "you have
 * no IPAs".
 */
export async function fetchEntitledIpas(
  ppApiBaseUrl: string,
  bearer: string,
): Promise<EntitledIpaEntry[]> {
  const res = await fetch(`${ppApiBaseUrl}/ProviderOverview/EntitledIpas`, {
    method: "POST",
    headers: { Authorization: bearer },
  });

  if (!res.ok) {
    throw new EntitledIpasError(res.status);
  }

  const data: unknown = await res.json();
  const ipas = (data as { ipas?: unknown } | null)?.ipas;
  if (!Array.isArray(ipas)) return [];

  return ipas
    .map((entry): EntitledIpaEntry => {
      const e = entry as { ipa?: unknown; providerNpis?: unknown };
      const ipa = typeof e.ipa === "string" ? e.ipa : "";
      const providerNpis = Array.isArray(e.providerNpis)
        ? e.providerNpis.filter((n): n is string => typeof n === "string")
        : [];
      return { ipa, providerNpis };
    })
    .filter((e) => e.ipa.length > 0);
}
